import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, useMemo } from "react";

import type { AssistantRoute } from "../../api/client";
import { useUi } from "../../state/ui";
import type { LonLat } from "../graphics/geometry";
import { revealPaint, revealStyle, useFadeIn } from "../reveal";
import { t } from "../../i18n/i18n";
import { chevron } from "../terrain/TerrainLayers";
import { FRIEND_BLUE } from "../units/symbols";

/** Itinéraire tracé par l'assistant (outil de l'iPad « itinéraire en évitant l'ENI », registre E23), dans le
 *  langage de l'axe favorable de l'analyse du terrain : bleu ami tireté (mouvement prévu) sur un liseré blanc,
 *  chevron à l'arrivée, tronçons exposés soulignés du rouge ennemi — plus larges au-delà de la moitié de la
 *  menace maximale —, départ et arrivée nommés. Seul ajout à la carte figée (accord d'Alexis du 30/09/2026). */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

const HOSTILE_RED = "#C83E3E";
const BELOW = "graphics-fill";
const FADE_MS = 450;

function build(route: AssistantRoute): FeatureCollection {
  const features: Feature[] = route.runs.map((run) => ({
    type: "Feature",
    properties: { level: run.level },
    geometry: { type: "LineString", coordinates: run.coords },
  }));
  const all = route.runs.flatMap((r) => r.coords) as LonLat[];
  const head = chevron(all, 260);
  if (head) features.push({ type: "Feature", properties: { level: 0, head: true }, geometry: { type: "LineString", coordinates: head } });
  return { type: "FeatureCollection", features };
}

function EndTag({ label, name }: { label: string; name: string }) {
  return (
    <div className="pointer-events-none flex h-5 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
      <span className="text-[10px] leading-none font-semibold tracking-[0.04em] text-ink-muted uppercase">{label}</span>
      <span className="text-[12px] leading-none font-semibold text-ink">{name}</span>
    </div>
  );
}

export function AssistantRouteLayers({ Source, Layer, Marker }: EngineParts) {
  const route = useUi((s) => s.assistantRoute);
  const data = useMemo(() => (route ? build(route) : null), [route]);
  const shown = useFadeIn(route);
  if (!route || !data) return null;
  const fade = (type: string, paint: Record<string, unknown>) => revealPaint(type, paint, shown, FADE_MS);

  return (
    <>
      <Source id="gx-assistant-route" type="geojson" data={data}>
        <Layer id="assistant-route-exposure" type="line" beforeId={BELOW} filter={[">=", ["get", "level"], 1]}
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={fade("line", { "line-color": HOSTILE_RED, "line-width": ["match", ["get", "level"], 2, 14, 10], "line-opacity": 0.35 })} />
        <Layer id="assistant-route-casing" type="line" beforeId={BELOW} layout={{ "line-cap": "round", "line-join": "round" }}
          paint={fade("line", { "line-color": "#ffffff", "line-width": 7 })} />
        <Layer id="assistant-route" type="line" beforeId={BELOW} layout={{ "line-join": "round" }}
          paint={fade("line", { "line-color": FRIEND_BLUE, "line-width": 4, "line-dasharray": [2.5, 1.2] })} />
      </Source>
      {[
        { key: "origin", label: t("Départ"), end: route.origin },
        { key: "destination", label: t("Arrivée"), end: route.destination },
      ].map(({ key, label, end }) => (
        <Marker key={key} longitude={end.lon} latitude={end.lat} anchor="bottom" offset={[0, -6]}
          style={{ pointerEvents: "none", ...revealStyle(shown, FADE_MS) }}>
          <EndTag label={label} name={end.name} />
        </Marker>
      ))}
    </>
  );
}
